import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Higher-Order Component that injects navigation functions into class components
 * This HOC provides:
 * - navigate: function for programmatic navigation (replaces history.push)
 * - location: current location object
 * - navigateBack: function to go back in history (replaces history.goBack)
 * - navigateReplace: function to replace current route (replaces history.replace)
 * - match: object with params property (replaces React Router v5 match prop)
 */
const withNavigate = (WrappedComponent) => {
    const WithNavigateComponent = (props) => {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();

        // Create navigation helper functions
        const navigationProps = {
            navigate: (to, options) => {
                try {
                    navigate(to, options);
                } catch {
                    // Fallback: try to navigate to home or handle gracefully
                    navigate('/');
                }
            },
            navigateBack: () => {
                try {
                    navigate(-1);
                } catch {
                    // Fallback: navigate to home
                    navigate('/');
                }
            },
            navigateReplace: (to, options = {}) => {
                try {
                    navigate(to, { ...options, replace: true });
                } catch {
                    navigate('/');
                }
            },
            location,
            // Provide match object similar to React Router v5 for backward compatibility
            match: {
                params,
            },
        };

        return <WrappedComponent {...props} {...navigationProps} />;
    };

    // Set display name for debugging
    WithNavigateComponent.displayName = `withNavigate(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return WithNavigateComponent;
};

export default withNavigate;
